import {
  type BrandBackdropOptions,
  createBrandBackdrop,
} from '@aecom-ai-engineering/design-styles/brand/backdrop';
import { type FC, useEffect, useRef } from 'react';

interface BrandBackdropProps {
  /**
   * Utilities on the canvas itself. Backdrop.css supplies the positioning, so
   * this is for what it does not cover — a mask over an evenly covering
   * `field`, say.
   */
  className?: string;
  /**
   * `edges` ramps in from both sides and leaves the middle clear for copy.
   * `halo` is a dome on the bottom edge. `field` covers evenly.
   */
  shape: BrandBackdropOptions['shape'];
}

/**
 * The design system's dithered brand field, sized to its positioned parent.
 *
 * The renderer is framework-agnostic and lives in design-styles, so this is the
 * entire React side of it: create on mount, tear down on unmount.
 *
 * Not `BrandBackdrop` from react-components, which is the same few lines but
 * would need 1.13.0, and that carries the design system's Excel DocumentViewer
 * into an app landing its own.
 *
 * `data-ds-brand-backdrop` is the contract: brand/Backdrop.css selects on it and
 * supplies the absolute positioning, so there is no className here.
 */
export const BrandBackdrop: FC<BrandBackdropProps> = ({ className, shape }) => {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvas.current) return;

    const backdrop = createBrandBackdrop(canvas.current, { shape });

    return () => backdrop.destroy();
  }, [shape]);

  return <canvas aria-hidden className={className} data-ds-brand-backdrop ref={canvas} />;
};
